import { blankDocument, blankRegion, blankWidget, nextId } from './layoutSchema.js';
import type { LayoutDocument, LayoutRegion } from './types.js';

/** A fractional box on the canvas: x/y/w/h are 0..1. `ticker` regions start with a running-text widget. */
interface Slot {
  x: number;
  y: number;
  w: number;
  h: number;
  name: string;
  ticker?: boolean;
}

export interface LayoutTemplate {
  key: string;
  label: string;
  description: string;
  slots: Slot[];
}

/** Ready-made arrangements a new layout can start from. Mirrors the portable CMS's starters. */
export const LAYOUT_TEMPLATES: LayoutTemplate[] = [
  { key: 'full', label: 'Full screen', description: 'One region over the whole screen.', slots: [{ x: 0, y: 0, w: 1, h: 1, name: 'Main' }] },
  {
    key: 'split',
    label: 'Split',
    description: 'Two equal halves, side by side.',
    slots: [
      { x: 0, y: 0, w: 0.5, h: 1, name: 'Left' },
      { x: 0.5, y: 0, w: 0.5, h: 1, name: 'Right' },
    ],
  },
  {
    key: 'l-shape',
    label: 'L-shape',
    description: 'Main content, a side column and a ticker under both.',
    slots: [
      { x: 0, y: 0, w: 0.75, h: 0.86, name: 'Main' },
      { x: 0.75, y: 0, w: 0.25, h: 0.86, name: 'Side' },
      { x: 0, y: 0.86, w: 1, h: 0.14, name: 'Ticker', ticker: true },
    ],
  },
  {
    key: 'banner',
    label: 'Banner + main',
    description: 'A header strip over the main content.',
    slots: [
      { x: 0, y: 0, w: 1, h: 0.18, name: 'Banner' },
      { x: 0, y: 0.18, w: 1, h: 0.82, name: 'Main' },
    ],
  },
  {
    key: 'grid',
    label: 'Grid 2×2',
    description: 'Four equal regions.',
    slots: [
      { x: 0, y: 0, w: 0.5, h: 0.5, name: 'Top left' },
      { x: 0.5, y: 0, w: 0.5, h: 0.5, name: 'Top right' },
      { x: 0, y: 0.5, w: 0.5, h: 0.5, name: 'Bottom left' },
      { x: 0.5, y: 0.5, w: 0.5, h: 0.5, name: 'Bottom right' },
    ],
  },
];

export function findTemplate(key: string | null | undefined): LayoutTemplate | undefined {
  return LAYOUT_TEMPLATES.find((t) => t.key === key);
}

function regionFor(slot: Slot, index: number, width: number, height: number): LayoutRegion {
  const region = blankRegion(width, height);
  region.name = slot.name;
  region.zIndex = index;
  region.rect = {
    unit: 'px',
    x: Math.round(slot.x * width),
    y: Math.round(slot.y * height),
    width: Math.round(slot.w * width),
    height: Math.round(slot.h * height),
  };
  if (slot.ticker) region.playlist = { id: nextId('pl'), items: [blankWidget('text')] };
  return region;
}

/** A fresh document (new ids every call) laid out as the template; blank for an unknown key. */
export function templateDocument(key: string | null | undefined, width = 1920, height = 1080): LayoutDocument {
  const template = findTemplate(key);
  if (!template) return blankDocument(width, height);
  const doc = blankDocument(width, height);
  doc.regions = template.slots.map((slot, i) => regionFor(slot, i, width, height));
  return doc;
}
